import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { SettingsLayout } from './SettingsLayout';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Plus, Trash2, Copy, Check } from 'lucide-react';
import { API_URL } from '../../lib/api';
import { GradientBorder } from '../../components/ui/gradient-border';

interface ApiKeyItem {
  id: string;
  keyPrefix: string;
  name: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export function SettingsApiKeysPage() {
  const { isAuthenticated } = useAuth();
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [copied, setCopied] = useState(false);

  // Revoke state
  const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchKeys();
    }
  }, [isAuthenticated]);

  async function fetchKeys() {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/v2/cloud/api-keys`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch API keys');
      const data = await res.json();
      setKeys(data.keys);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!newKeyName.trim() || creating) return;
    setCreating(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/v2/cloud/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newKeyName.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create key');
      }

      const data = await res.json();
      setNewKeyValue(data.key);
      await fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
      setShowCreateModal(false);
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    if (!revokeKeyId || revoking) return;
    setRevoking(true);

    try {
      const res = await fetch(
        `${API_URL}/api/v2/cloud/api-keys/${revokeKeyId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );

      if (!res.ok) throw new Error('Failed to revoke key');
      await fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    } finally {
      setRevoking(false);
      setRevokeKeyId(null);
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(newKeyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function closeCreateModal() {
    setShowCreateModal(false);
    setNewKeyName('');
    setNewKeyValue('');
    setCopied(false);
  }

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <SettingsLayout activeTab="api-keys">
      <div>
        <h1 className="text-2xl font-semibold text-white mb-2">API Keys</h1>
        <p className="text-white/60 text-sm mb-4">
          API keys let your coding agents access Miles on your behalf.
          When you use the Miles skill for the first time, a key is created
          automatically. You can also create keys manually here.
        </p>
        <div className="mb-6">
          <Button
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="h-4 w-4" />
            Create Key
          </Button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-white/50">Loading keys...</div>
        ) : keys.length === 0 ? (
          null
        ) : (
          <>
          <hr className="border-white/10 mb-6" />
          <h2 className="text-lg font-semibold text-white mb-4">Active API Keys</h2>
          <div className="space-y-3">
            {activeKeys.map((key) => (
              <GradientBorder key={key.id}>
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-medium">{key.name}</span>
                      <code className="text-xs text-white/50 bg-white/5 px-2 py-0.5 rounded">
                        {key.keyPrefix}...
                      </code>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-white/50">
                      <span>
                        Created{' '}
                        {new Date(key.createdAt).toLocaleDateString()}
                      </span>
                      {key.lastUsedAt && (
                        <span>
                          Last used{' '}
                          {new Date(key.lastUsedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRevokeKeyId(key.id)}
                    className="ml-4 text-white/50 hover:text-red-400 hover:bg-red-500/10"
                    title="Revoke key"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </GradientBorder>
            ))}

            {revokedKeys.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <span className="text-xs text-white/50 uppercase tracking-wider">
                    Revoked
                  </span>
                </div>
                {revokedKeys.map((key) => (
                  <div
                    key={key.id}
                    className="rounded-xl border border-white/5 bg-card/50 p-4 opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-white/60 font-medium line-through">
                        {key.name}
                      </span>
                      <code className="text-xs text-white/20 bg-white/5 px-2 py-0.5 rounded">
                        {key.keyPrefix}...
                      </code>
                    </div>
                    <div className="text-xs text-white/20 mt-1">
                      Revoked{' '}
                      {key.revokedAt &&
                        new Date(key.revokedAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          </>
        )}
      </div>

      {/* Create Key Modal */}
      <AlertDialog open={showCreateModal} onOpenChange={closeCreateModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {newKeyValue ? 'API Key Created' : 'Create API Key'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {newKeyValue
                ? 'Copy your API key now. You won\'t be able to see it again.'
                : 'Give your key a name so you can identify it later.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {newKeyValue ? (
            <div className="my-4">
              <div className="flex items-center gap-2 bg-neutral-900 border border-white/10 rounded-lg p-3">
                <code className="flex-1 text-sm text-white/90 break-all font-mono">
                  {newKeyValue}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="my-4">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., My CLI Key"
                className="w-full rounded-lg bg-neutral-900 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate();
                }}
              />
            </div>
          )}

          <AlertDialogFooter>
            {newKeyValue ? (
              <Button onClick={closeCreateModal}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeCreateModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!newKeyName.trim() || creating}
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Confirmation */}
      <AlertDialog
        open={!!revokeKeyId}
        onOpenChange={() => setRevokeKeyId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately invalidate this API key. Any applications
              using it will lose access. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setRevokeKeyId(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRevoke}
              disabled={revoking}
            >
              {revoking ? 'Revoking...' : 'Revoke Key'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsLayout>
  );
}
